import gql from 'graphql-tag';
import { v4 as uuidv4 } from 'uuid';
import {
  contentDetailsFields,
  contentListingFields,
  contentViewFields,
} from './ContentFields';
import {
  ContentAttributesSchema,
  ContentAttributeValuesSchema,
  ContentSchema,
  ContentsSchema,
  SalesActionSchema,
} from '../../definitions/schemas';
import { getExtension } from '../../lib/File';
import CloudStorage from '../../Services/CloudStorage';
import {
  ProgressCallback,
  UploadContext,
} from '../../Services/Content/CreateParams';
import Interface from '../Interface';
import ContentType from '../../enums/ContentType';
import { salesActionsFields } from '../SalesActions/SalesActionsFields';

interface GetContentInput {
  id: string;
}

interface GetContentData {
  getContent: ContentSchema;
}

interface GetCallAiContentSignedUrlData {
  getCallAiContentSignedUrl: string;
}

interface ContentsData {
  contents: ContentsSchema;
}

interface AcceptContentInput {
  id: string;
}

interface DeleteContentInput {
  archived?: boolean;
  id: string;
}

interface ParseOpenGraphData {
  parseOpenGraph: {
    // Dynamic json of OpenGraph data
    openGraph: Record<string, string>;
  };
}

interface SyncContentInput {
  id: string;
}

interface MtDataInput {
  provider?: string;
  contentId?: string;
  isSnippet?: boolean;
  snippetInfo?: {
    id?: string;
    startTime?: string;
    endTime?: string;
  };
}

export interface CreateContentInput {
  contactId?: string;
  description?: string;
  filename?: string;
  html?: boolean;
  iframe?: boolean;
  libraryVisible?: boolean;
  mime?: string;
  mtData?: MtDataInput;
  name?: string | null;
  openGraph?: Record<string, string>;
  pages?: number;
  parentId?: string;
  personal?: boolean;
  questionId?: string;
  requestedReferenceId?: string;
  sectionHeaderId?: string;
  storageProviderFileId?: string;
  storageSyncPreference?: string;
  tags?: string[] | null;
  text?: string;
  totalSeconds?: number;
  type: ContentType;
  url?: string;
  expiresAt?: number;
}

interface UpdateContentInput {
  id: string;
  attributes: ContentAttributesSchema;
  cover: string | null;
  coverAssetId?: string | null;
  coverType?: string;
  description: string | null;
  downloadable: boolean;
  iframe?: boolean;
  name: string;
  personal: boolean;
  sectionHeaderId?: string;
  shareable: boolean;
  status: string;
  storageSyncPreference?: string;
  tags: string[];
  text?: string;
}

interface CreateVersionInput {
  filename?: string;
  html?: boolean;
  id: string;
  mime?: string;
  name?: string;
  text?: string;
  totalSeconds?: number;
  url?: string;
}

interface CreateVersionData {
  content: ContentSchema;
}

interface PrepareContentInput {
  ids: string[];
  source?: string;
  sourceType: string;
  skip: boolean;
}

export interface SearchFilters {
  archived?: boolean;
  attributes?: ContentAttributeValuesSchema[];
  fromCreatedAt?: number | null;
  personal?: boolean;
  search?: string | null;
  shareable?: boolean;
  status?: string[];
  storageSyncPreference?: string[];
  storageSyncStatus?: string[];
  tags?: string[];
  toCreatedAt?: number | null;
  users?: string[];
}

interface PreparedCopyInput {
  contentId: string;
}

interface PreparedCopyData {
  copy: {
    id: string;
    name: string;
    link: string;
  };
}

interface CopyEntitiesInput {
  contentIds?: string[];
  sourceType: string;
  source?: string;
  buttonIds?: string[];
}

export default class ContentInterface extends Interface {
  /**
   * @deprecated
   */
  public async createLink(contentId: string) {
    return this.mutate<ContentSchema>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation createContentLink($input: CreateContentLinkInput!) {
            createContentLink(input: $input) {
              errors
              message
              content {
                link
              }
            }
          }
        `,
        variables: {
          input: { contentId },
        },
      });

      const { errors, message, content } = response.data.createContentLink;

      return {
        data: content,
        serverErrors: errors,
        serverMessage: message,
      };
    }, 'Error creating link');
  }

  public create(input: CreateContentInput) {
    return this.mutate<ContentSchema>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
            mutation createContent($input: CreateContentInput!) {
              createContent(input: $input) {
                errors
                message
                content {
                  ${contentViewFields}
                  tags
                }
              }
            }
          `,
        variables: {
          input,
        },
      });

      const { errors, message, content } = response.data.createContent;

      return {
        data: content,
        serverErrors: errors,
        serverMessage: message,
      };
    }, 'Error creating content');
  }

  public async createVersion(input: CreateVersionInput) {
    return this.mutate<CreateVersionData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation createContentVersion(
            $input: CreateContentVersionInput!
          ) {
            createContentVersion(input: $input) {
              errors
              message
              content {
                ${contentDetailsFields}
              }
            }
          }
        `,
        variables: { input },
      });

      const { errors, message, content } = response.data.createContentVersion;

      return {
        data: { content },
        serverErrors: errors,
        serverMessage: message,
      };
    }, 'Error creating new content version');
  }

  /**
   * Current use cases:
   *  - Public content (public links)
   * @deprecated
   */
  public async getForPublic(slug: string) {
    const data = await this.query<GetContentData>(
      () =>
        this.apolloService.executeQuery<GetContentData>({
          query: gql`
          query getContent($slug: String!) {
            getContent(slug: $slug) {
              ${contentViewFields}
              organization {
                id
                logo
                name
              }
            }
          }
        `,
          variables: { slug },
        }),
      'Error loading content'
    );
    return data.getContent;
  }

  /**
   * Current use cases:
   *  - Get Call AI MT Content signed URL(public links)
   */
  public async getCallAiContentSignedUrl(
    contentId: string,
    callAiShareId: string
  ) {
    const data = await this.query<GetCallAiContentSignedUrlData>(
      () =>
        this.apolloService.executeQuery<GetCallAiContentSignedUrlData>({
          query: gql`
            query getCallAiContentSignedUrl($callAiShareId: ID!, $id: ID!) {
              getCallAiContentSignedUrl(callAiShareId: $callAiShareId, id: $id)
            }
          `,
          variables: { id: contentId, callAiShareId },
        }),
      'Error loading content signed url',
      true
    );

    return `https:${data.getCallAiContentSignedUrl}`;
  }

  /**
   * Current use cases:
   *  - Bulk update
   *  - Create room (review step)
   */
  public async getByIds(
    ids: string[],
    fields = contentDetailsFields,
    withDeleted = false,
    archived?: boolean
  ) {
    const data = await this.query<ContentsData>(
      () =>
        this.apolloService.executeQuery<ContentsData>({
          query: gql`
        query contents(
          $ids: [ID!]
          $archived: Boolean
          $withDeleted: Boolean
        ) {
          contents(
            ids: $ids
            archived: $archived
            withDeleted: $withDeleted
          ) {
            ${fields}
          }
        }
      `,
          variables: {
            ids,
            withDeleted,
            archived,
          },
        }),
      'Error loading contents'
    );

    return data.contents;
  }

  /**
   * Current use cases:
   *  - Library
   *  - Create room (select content step)
   *  - Insights
   */
  public async search(filters: SearchFilters, fields = contentListingFields) {
    const data = await this.query<ContentsData>(
      () =>
        this.apolloService.executeQuery<ContentsData>({
          query: gql`
          query contents(
            $archived: Boolean
            $attributes: [SearchReferenceAttributeInput!]
            $fromCreatedAt: Int
            $libraryVisible: Boolean
            $personal: Boolean
            $search: String
            $shareable: Boolean
            $status: [String!]
            $storageSyncPreference: [String!]
            $storageSyncStatus: [String!]
            $tags: [String!]
            $toCreatedAt: Int
            $users: [ID!]
          ) {
            contents(
              archived: $archived
              attributes: $attributes
              fromCreatedAt: $fromCreatedAt
              libraryVisible: $libraryVisible
              personal: $personal
              search: $search
              shareable: $shareable
              status: $status
              storageSyncPreference: $storageSyncPreference
              storageSyncStatus: $storageSyncStatus
              tags: $tags
              toCreatedAt: $toCreatedAt
              users: $users
            ) {
              ${fields}
            }
          }
        `,
          variables: {
            ...filters,
            // load only library visible content
            libraryVisible: true,
          },
        }),
      'Error loading library content'
    );

    return data.contents;
  }

  /**
   * Current use cases:
   *  - Content details
   *  - Content details editor
   *  - Content preview
   */
  public async get(variables: GetContentInput, fields = contentDetailsFields) {
    const data = await this.query<GetContentData>(
      () =>
        this.apolloService.executeQuery<GetContentData>({
          query: gql`
          query getContent($id: ID) {
            getContent(id: $id) {
              ${fields}
            }
          }
        `,
          variables,
        }),
      'Error loading content'
    );

    return data.getContent;
  }

  public accept(input: AcceptContentInput) {
    return this.mutate<null>(async () => {
      const {
        data: { acceptContent },
      } = await this.apolloService.executeMutation({
        mutation: gql`
          mutation acceptContent($input: AcceptContentInput!) {
            acceptContent(input: $input) {
              errors
              details
            }
          }
        `,
        variables: {
          input: {
            id: +input.id,
          },
        },
      });
      return {
        data: null,
        serverErrors: acceptContent.errors,
        serverMessage: acceptContent.message,
      };
    }, 'Error deleting content');
  }

  public update(input: UpdateContentInput, fields = contentViewFields) {
    return this.mutate<ContentSchema>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation updateContent($input: UpdateContentInput!) {
            updateContent(input: $input) {
              errors
              message
              content {
                ${fields}
                tags
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      const { errors, message, content } = response.data.updateContent;

      return {
        data: content,
        serverErrors: errors,
        serverMessage: message,
      };
    }, 'Error updating content');
  }

  public delete(input: DeleteContentInput) {
    return this.mutate<null>(async () => {
      const {
        data: { deleteContent },
      } = await this.apolloService.executeMutation({
        mutation: gql`
          mutation deleteContent($input: DeleteContentInput!) {
            deleteContent(input: $input) {
              errors
              message
            }
          }
        `,
        variables: { input },
      });
      return {
        data: null,
        serverErrors: deleteContent.errors,
        serverMessage: deleteContent.message,
      };
    }, 'Error deleting content');
  }

  public static uploadCover(
    id: string,
    uploadContext: UploadContext,
    file: File,
    progressCallback?: ProgressCallback
  ) {
    return CloudStorage.uploadImage(
      uploadContext,
      file,
      `reference-${id}/cover-${Date.now()}-${uuidv4()}.${getExtension(file)}`,
      progressCallback
    );
  }

  public approveSync(input: SyncContentInput) {
    return this.mutate<null>(async () => {
      const {
        data: { approveContentSyncVersion },
      } = await this.apolloService.executeMutation({
        mutation: gql`
          mutation approveContentSyncVersion($input: ApproveVersionInput!) {
            approveContentSyncVersion(input: $input) {
              errors
            }
          }
        `,
        variables: {
          input,
        },
      });
      return {
        data: null,
        serverErrors: approveContentSyncVersion.errors,
        serverMessage: approveContentSyncVersion.message,
      };
    }, 'Error approving sync content');
  }

  public rejectSync(input: SyncContentInput) {
    return this.mutate<null>(async () => {
      const {
        data: { rejectContentSyncVersion },
      } = await this.apolloService.executeMutation({
        mutation: gql`
          mutation rejectContentSyncVersion($input: RejectVersionInput!) {
            rejectContentSyncVersion(input: $input) {
              errors
            }
          }
        `,
        variables: {
          input,
        },
      });
      return {
        data: null,
        serverErrors: rejectContentSyncVersion.errors,
        serverMessage: rejectContentSyncVersion.message,
      };
    }, 'Error rejecting sync content');
  }

  public syncNow(input: SyncContentInput) {
    return this.mutate<null>(async () => {
      const {
        data: { contentStorageSync },
      } = await this.apolloService.executeMutation({
        mutation: gql`
          mutation contentStorageSync($input: ContentSyncInput!) {
            contentStorageSync(input: $input) {
              errors
            }
          }
        `,
        variables: {
          input,
        },
      });
      return {
        data: null,
        serverErrors: contentStorageSync.errors,
        serverMessage: contentStorageSync.message,
      };
    }, 'Error sync content');
  }

  public async prepareContents(
    input: PrepareContentInput,
    fields = contentDetailsFields
  ) {
    return this.mutate<ContentSchema[]>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation prepareContents($input: PrepareContentsInput!) {
            prepareContents(input: $input) {
              errors
              message
              contents {
                ${fields}
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      const { errors, message, contents } = response.data.prepareContents;

      return {
        data: contents,
        serverErrors: errors,
        serverMessage: message,
      };
    }, 'Error loading contents');
  }

  public async parseOpenGraph(url: string) {
    const data = await this.query<ParseOpenGraphData>(
      () =>
        this.apolloService.executeQuery<ParseOpenGraphData>({
          query: gql`
            query parseOpenGraph($url: String!) {
              parseOpenGraph(url: $url) {
                openGraph
              }
            }
          `,
          variables: { url },
        }),
      'Error parsing url'
    );
    return data.parseOpenGraph.openGraph;
  }

  public async copyDocument(input: PreparedCopyInput) {
    return this.mutate<PreparedCopyData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation prepareEditableCopy($input: PrepareEditableCopyInput!) {
            prepareEditableCopy(input: $input) {
              errors
              copy {
                id
                name
                link
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: response.data.prepareEditableCopy,
        serverErrors: response.errors,
        serverMessage: response.errors?.map((e) => e.message).join(''),
      };
    }, 'Error creating a copy');
  }

  // Add the method to the ContentInterface class
  public async copyEntities(input: CopyEntitiesInput) {
    return this.mutate<{
      contents: ContentSchema[];
      buttons: SalesActionSchema[];
    }>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
            mutation copyEntities($input: CopyEntitiesInput!) {
              copyEntities(input: $input) {
                contents {
                  ${contentViewFields}
                }
                buttons {
                  ${salesActionsFields}
                }
                errors
                message
              }
            }
          `,
        variables: { input },
      });

      const { contents, buttons, errors, message } = response.data.copyEntities;

      return {
        data: { contents, buttons },
        serverErrors: errors,
        serverMessage: message,
      };
    }, 'Error copying entities');
  }
}
